import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { CalendarCheck, Gauge, ShieldCheck, Smartphone, Zap } from "lucide-react";
import { EASE } from "@/lib/site";

const pop = (d) => ({
  initial: { opacity: 0, scale: 0.9, y: 30 },
  animate: { opacity: 1, scale: 1, y: 0 },
  transition: { duration: 1, delay: d, ease: EASE },
});

const BrowserCard = () => (
  <div className="glass overflow-hidden rounded-2xl shadow-[0_40px_80px_-30px_rgba(0,0,0,.9)]">
    <div className="flex items-center gap-2 border-b border-white/5 px-4 py-3">
      <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
      <span className="h-2.5 w-2.5 rounded-full bg-ws-lime/80" />
      <span className="ml-3 flex-1 truncate rounded-full bg-white/5 px-3 py-1 font-mono text-[10px] text-ws-muted">seunegocio.com.br</span>
    </div>
    <div className="grid grid-cols-5 gap-3 p-4 sm:p-5">
      <div className="col-span-3 space-y-2.5">
        <div className="h-2 w-16 rounded-full bg-ws-lime/70" />
        <div className="h-3.5 w-full rounded-md bg-white/80" />
        <div className="h-3.5 w-4/5 rounded-md bg-white/80" />
        <div className="h-2 w-11/12 rounded-full bg-white/15" />
        <div className="h-2 w-3/4 rounded-full bg-white/15" />
        <div className="flex gap-2 pt-2">
          <div className="h-6 w-20 rounded-full bg-ws-lime" />
          <div className="h-6 w-16 rounded-full border border-white/20" />
        </div>
      </div>
      <div className="col-span-2 flex items-end gap-1.5 rounded-xl border border-white/5 bg-black/30 p-3">
        {[60, 85, 45, 100, 70, 90].map((h, i) => (
          <span key={i} className="bar-anim flex-1 rounded-sm bg-gradient-to-t from-ws-deep to-ws-lime" style={{ height: `${h}%`, animationDelay: `${i * 0.35}s` }} />
        ))}
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className={`col-span-5 flex items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-2.5 sm:col-span-5 ${i > 0 ? "hidden sm:flex" : ""}`}>
          <span className="h-7 w-7 rounded-lg bg-ws-lime/15" />
          <span className="h-2 flex-1 rounded-full bg-white/15" />
          <span className="h-2 w-10 rounded-full bg-ws-lime/50" />
        </div>
      ))}
    </div>
  </div>
);

const PhoneCard = () => (
  <div className="w-[150px] rounded-[26px] border border-white/10 bg-[#0B100C] p-2 shadow-[0_30px_60px_-20px_rgba(0,0,0,.9)] sm:w-[178px]">
    <div className="rounded-[20px] bg-ws-card p-3">
      <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/15" />
      <p className="font-mono text-[9px] uppercase tracking-widest text-ws-muted">Agenda de hoje</p>
      {["09:00", "10:30", "14:00"].map((t, i) => (
        <div key={t} className={`mt-2 flex items-center gap-2 rounded-xl p-2 ${i === 1 ? "bg-ws-lime text-ws-bg" : "bg-white/[0.04] text-ws-text"}`}>
          <CalendarCheck size={12} />
          <span className="text-[10px] font-semibold">{t}</span>
          <span className={`h-1.5 flex-1 rounded-full ${i === 1 ? "bg-ws-bg/30" : "bg-white/15"}`} />
        </div>
      ))}
      <div className="mt-3 rounded-xl border border-ws-lime/30 bg-ws-lime/10 p-2 text-[9px] font-medium text-ws-glow">Agendamento confirmado</div>
    </div>
  </div>
);

const CodeCard = () => (
  <div className="glass rounded-2xl p-4 font-mono text-[10px] leading-relaxed sm:text-[11px]">
    <p><span className="text-ws-lime">const</span> <span className="text-ws-text">projeto</span> = <span className="text-ws-glow">criar</span>({"{"}</p>
    <p className="pl-3 text-ws-muted">ideia: <span className="text-[#E9F5D0]">"seu negócio"</span>,</p>
    <p className="pl-3 text-ws-muted">sob_medida: <span className="text-ws-lime">true</span></p>
    <p>{"}"});<span className="caret ml-0.5 inline-block h-3 w-1.5 translate-y-0.5 bg-ws-lime" /></p>
  </div>
);

const Chip = ({ icon: Icon, label }) => (
  <div className="glass flex items-center gap-2 rounded-full px-3 py-2 text-[11px] font-medium text-ws-text sm:text-xs">
    <span className="grid h-6 w-6 place-items-center rounded-full bg-ws-lime/15 text-ws-lime"><Icon size={13} /></span>
    {label}
  </div>
);

export const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div data-testid="hero-visual" onMouseMove={onMove} onMouseLeave={reset} className="relative mx-auto aspect-[1/1] w-full max-w-[560px] [perspective:1400px] sm:aspect-[6/5]">
      <motion.div {...pop(0.3)} className="pointer-events-none absolute inset-0 grid place-items-center">
        <div className="absolute h-[78%] w-[78%] rounded-full bg-ws-lime/[0.09] blur-[80px]" />
        <div className="spin-slow absolute h-[86%] w-[86%] rounded-full border border-dashed border-ws-lime/20" />
        <div className="spin-slower absolute h-[64%] w-[64%] rounded-full border border-ws-lime/10">
          <span className="absolute -top-1.5 left-1/2 h-3 w-3 rounded-full bg-ws-glow shadow-[0_0_20px_4px_rgba(124,255,26,.7)]" />
        </div>
      </motion.div>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="absolute inset-0">
        <motion.div {...pop(0.55)} className="absolute left-[4%] right-[10%] top-[14%]" style={{ transform: "translateZ(40px)" }}>
          <div className="float-y-slow"><BrowserCard /></div>
        </motion.div>
        <motion.div {...pop(0.8)} className="absolute bottom-[2%] left-0 sm:left-[-2%]" style={{ transform: "translateZ(110px)" }}>
          <div className="float-y" style={{ animationDelay: "-2s" }}><PhoneCard /></div>
        </motion.div>
        <motion.div {...pop(0.95)} className="absolute right-0 top-[0%] w-[200px] sm:w-[230px]" style={{ transform: "translateZ(90px)" }}>
          <div className="float-y" style={{ animationDelay: "-4s" }}><CodeCard /></div>
        </motion.div>
        <motion.div {...pop(1.1)} className="absolute bottom-[16%] right-[2%] flex flex-col items-end gap-2.5" style={{ transform: "translateZ(130px)" }}>
          <div className="float-y-slow"><Chip icon={Smartphone} label="100% responsivo" /></div>
          <div className="float-y" style={{ animationDelay: "-3s" }}><Chip icon={Gauge} label="Alta performance" /></div>
          <div className="float-y-slow hidden sm:block" style={{ animationDelay: "-1s" }}><Chip icon={ShieldCheck} label="Seguro" /></div>
        </motion.div>
        <motion.div {...pop(1.25)} className="absolute bottom-[4%] left-[42%] hidden sm:block" style={{ transform: "translateZ(60px)" }}>
          <div className="flex items-center gap-2 rounded-full bg-ws-lime px-3.5 py-2 text-xs font-semibold text-ws-bg shadow-[0_10px_40px_-8px_rgba(124,255,26,.8)]">
            <Zap size={13} /> No ar
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};
